import React from 'react';
import styles from './TeammateCard.module.scss';
import { useNavigate } from 'react-router-dom';
import { FILTERS } from '../../filters/filtersConfig';

// NOTE: We need a proper TeammateState definition
interface TeammateState {
  _id: string;
  nickname: string;
  toolsExperience: { tool: string; mep: number }[];
  areaOfExpertise: string[];
  productType: string[];
  projectType: string[];
  offeredGratification: string[];
  forGratification: string[];
  baseLocation: string[];
  locations?: string[];
  workingTypeRemote?: boolean;
}

interface TeammateCardProps {
    teammate: TeammateState;
    tokenAvailable: boolean;
    addTeammate?: () => Promise<void>;
    deleteTeammate?: () => Promise<void>;
    teammateAdded?: boolean;
    loading?: boolean;
    myTeammate?: boolean;
    isMyTeammate?: boolean;
}

const TeammateCard: React.FC<TeammateCardProps> = ({ 
    teammate, 
    tokenAvailable, 
    addTeammate, 
    deleteTeammate, 
    teammateAdded, 
    loading, 
    myTeammate = false,
    isMyTeammate = false
}) => {
    const navigate = useNavigate();

    const handleClick = () => {
        navigate(`/teammates/${teammate._id}`);
    };



    const renderStringArray = (items: string[] | undefined, maxVisible: number) => {
         if (!items || items.length === 0) {
            return <span>Not specified</span>;
        }

        // Jeśli jest więcej niż maxVisible elementów, spróbuj pokazać więcej w jednej linii
        if (items.length > maxVisible) {
            // Sprawdź czy możemy pokazać więcej elementów w jednej linii
            const extendedMax = Math.min(items.length, maxVisible + 2); // Dodaj 2 więcej
            const visibleItems = items.slice(0, extendedMax).join(', ');
            const remainingCount = items.length - extendedMax;
            
            if (remainingCount > 0) {
                return (
                    <span>
                        {visibleItems}
                        <span className={styles.moreTextContainer}>
                            <span className={styles.moreText}>, +{remainingCount} more</span>
                            <div className={styles.tooltip}>
                                {items.slice(extendedMax).join(', ')}
                            </div>
                        </span>
                    </span>
                );
            } else {
                return <span>{visibleItems}</span>;
            }
        }

        // Jeśli jest maxVisible lub mniej elementów, połącz je przecinkami
        return <span>{items.join(', ')}</span>;
    };

    if (loading) {
        return <div className={styles.card}>Loading...</div>;
    }

    return (
        <div className={styles.card} onClick={handleClick}>
            <div className={styles.header}>
                <h2 className={styles.title}>{teammate.nickname}</h2>
                <div className={styles.actions}>
                    {myTeammate && (
                        <button
                            className={styles.editButton}
                            onClick={e => {
                                e.stopPropagation();
                                navigate(`/teammates/editTeammate/${teammate._id}`);
                            }}
                        >
                            Edit
                        </button>
                    )}
                    {isMyTeammate ? (
                        <div className={styles.myTeammateBadge}>
                            <span>My Skills</span>
                        </div>
                    ) : tokenAvailable && (
                        <>
                            {!teammateAdded ? (
                                <button 
                                    className={styles.saveButton} 
                                    onClick={(e) => { e.stopPropagation(); addTeammate?.(); }}
                                >
                                    Save
                                </button>
                            ) : (
                                <button 
                                    className={styles.forgetButton} 
                                    onClick={(e) => { e.stopPropagation(); deleteTeammate?.(); }}
                                >
                                    Forget
                                </button>
                            )}
                        </>
                    )}
                    <button 
                        className={styles.actionButton} 
                        onClick={(e) => { e.stopPropagation(); handleClick(); }}
                    >
                        See more
                    </button>
                </div>
            </div>
            
            <div className={styles.detailsSection}>
                <div className={styles.detailsRow}>
                    <strong>{FILTERS.projectType.label}:</strong>
                    <span> {renderStringArray(teammate.projectType, 2)}</span>
                </div>
                <div className={styles.detailsRow}>
                    <strong>{FILTERS.productType ? FILTERS.productType.label : 'Product Type'}:</strong>
                    <span> {renderStringArray(teammate.productType, 3)}</span>
                </div>
                <div className={styles.detailsRow}>
                    <strong>{FILTERS.offeredGratification.label}:</strong>
                    <span> {renderStringArray(teammate.offeredGratification, 3)}</span>
                </div>
                <div className={styles.detailsRow}>
                    <strong>{FILTERS.forGratification.label}:</strong>
                    <span> {renderStringArray(teammate.forGratification, 3)}</span>
                </div>
                <div className={styles.detailsRow}>
                    <strong>{FILTERS.toolsExperience.label}:</strong>
                    <span> {renderStringArray(teammate.toolsExperience?.map(exp => exp.tool) || [], 4)}</span>
                </div>
            </div>

            <div className={styles.footer}>
                <span>{FILTERS.locations.label}: {
                    teammate.locations && teammate.locations.length > 0 
                        ? renderStringArray(teammate.locations, 2)
                        : (teammate.workingTypeRemote ? 'Remote' : 'Not specified')
                }</span>
                <span className={styles.moreText}>See more</span>
            </div>
        </div>
    );
};

export default TeammateCard; 